import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, setDoc, addDoc, collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../index";

const ADMIN_PASSWORD = "ohadadmin"; // Same password as AdminSiumViewer

const typeOptions = [
    { value: "nose", label: "נושא" },
    { value: "helek", label: "חלק" },
    { value: "kereh", label: "כרך" },
    { value: "siman", label: "סימן" },
    { value: "saif", label: "סעיף" },
    { value: "perek", label: "פרק" },
    { value: "daf", label: "דף" },
    { value: "mizmor", label: "מזמור" },
    { value: "tora", label: "תורה" },
    { value: "shaar", label: "שער" },
    { value: "masehet", label: "מסכת" },
    { value: "sefer", label: "ספר" },
    { value: "seder", label: "סדר" },
    { value: "halahot", label: "הלכות"},
    { value: "parashat", label: "פרשת" },
    { value: "mishna", label: "משנה" },
    { value: "alia", label: "עליה" },
];

const generateId = () => Math.random().toString(36).substr(2, 9);

// Hebrew letter values for conversion
const hebrewLetters = {
    'א': 1, 'ב': 2, 'ג': 3, 'ד': 4, 'ה': 5, 'ו': 6, 'ז': 7, 'ח': 8, 'ט': 9,
    'י': 10, 'כ': 20, 'ל': 30, 'מ': 40, 'נ': 50, 'ס': 60, 'ע': 70, 'פ': 80, 'צ': 90,
    'ק': 100, 'ר': 200, 'ש': 300, 'ת': 400
};

const hebrewUnits = ['', 'א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט'];
const hebrewTens = ['', 'י', 'כ', 'ל', 'מ', 'נ', 'ס', 'ע', 'פ', 'צ'];
const hebrewHundreds = ['', 'ק', 'ר', 'ש', 'ת'];

// Convert Hebrew number string to integer
const hebrewToNumber = (hebrew) => {
    if (!hebrew || typeof hebrew !== 'string') return 0;
    let total = 0;
    for (const char of hebrew) {
        if (hebrewLetters[char]) {
            total += hebrewLetters[char];
        }
    }
    return total;
};

// Convert integer to Hebrew number string
const numberToHebrew = (num) => {
    if (num <= 0 || num > 999) return '';

    let result = '';

    // Handle hundreds
    const hundreds = Math.floor(num / 100);
    if (hundreds > 0 && hundreds <= 4) {
        result += hebrewHundreds[hundreds];
    } else if (hundreds > 4) {
        // For 500+, use combinations like תק, תר, תש, תת
        const remaining = hundreds - 4;
        result += 'ת' + hebrewHundreds[remaining];
    }

    num = num % 100;

    // Special cases for 15 and 16
    if (num === 15) {
        result += 'טו';
        return result;
    }
    if (num === 16) {
        result += 'טז';
        return result;
    }

    // Handle tens
    const tens = Math.floor(num / 10);
    if (tens > 0) {
        result += hebrewTens[tens];
    }

    // Handle units
    const units = num % 10;
    if (units > 0) {
        result += hebrewUnits[units];
    }

    return result;
};

// Generate array of Hebrew numbers from start to end
const generateHebrewRange = (startHebrew, endHebrew) => {
    const startNum = hebrewToNumber(startHebrew);
    const endNum = hebrewToNumber(endHebrew);

    if (startNum <= 0 || endNum <= 0 || startNum > endNum) {
        return [];
    }

    const result = [];
    for (let i = startNum; i <= endNum; i++) {
        result.push(numberToHebrew(i));
    }
    return result;
};

// Validate if string contains only Hebrew letters used in numbering
const isValidHebrewNumber = (str) => {
    if (!str || typeof str !== 'string') return false;
    const validChars = /^[אבגדהוזחטיכלמנסעפצקרשת]+$/;
    return validChars.test(str);
};

// Validate Hebrew range
const validateHebrewRange = (from, to) => {
    if (!isValidHebrewNumber(from) || !isValidHebrewNumber(to)) {
        return { valid: false, error: 'יש להזין אותיות עבריות בלבד' };
    }
    const fromNum = hebrewToNumber(from);
    const toNum = hebrewToNumber(to);
    if (fromNum <= 0 || toNum <= 0) {
        return { valid: false, error: 'ערך לא תקין' };
    }
    if (fromNum > toNum) {
        return { valid: false, error: 'הערך ההתחלתי חייב להיות קטן או שווה לערך הסופי' };
    }
    if (toNum - fromNum > 500) {
        return { valid: false, error: 'הטווח גדול מדי (מקסימום 500)' };
    }
    return { valid: true, error: null };
};

// Count children recursively
const countChildren = (node) => {
    if (!node.children || node.children.length === 0) return 0;
    let count = node.children.length;
    node.children.forEach(child => {
        count += countChildren(child);
    });
    return count;
};

const TreeNode = ({ node, index, depth, onAddSibling, onAddChild, onUpdate, onDelete, onGenerateRange, onGenerateFromComma, onToggleCollapse, collapsedNodes, parentType }) => {
    const maxDepth = 4;
    const canAddChild = depth < maxDepth;
    const hasChildren = node.children && node.children.length > 0;
    const isCollapsed = collapsedNodes.has(node.id);
    const childCount = countChildren(node);

    const [useRange, setUseRange] = useState(false);
    const [rangeType, setRangeType] = useState('hebrew'); // 'hebrew' or 'comma'
    const [rangeFrom, setRangeFrom] = useState('');
    const [rangeTo, setRangeTo] = useState('');
    const [rangeError, setRangeError] = useState('');
    const [commaInput, setCommaInput] = useState('');

    const handleTypeChange = (e) => {
        const newType = e.target.value;
        setUseRange(false);
        setRangeType('hebrew');
        setRangeFrom('');
        setRangeTo('');
        setRangeError('');
        setCommaInput('');
        onUpdate(node.id, { ...node, type: newType });
    };

    const handleNameChange = (e) => {
        onUpdate(node.id, { ...node, name: e.target.value });
    };

    const handleHebrewInput = (e, setter) => {
        const value = e.target.value;
        const filtered = value.replace(/[^אבגדהוזחטיכלמנסעפצקרשת]/g, '');
        setter(filtered);
        setRangeError('');
    };

    const handleRangeSubmit = () => {
        const validation = validateHebrewRange(rangeFrom, rangeTo);
        if (!validation.valid) {
            setRangeError(validation.error);
            return;
        }
        onGenerateRange(node.id, rangeFrom, rangeTo, node.type);
        setRangeFrom('');
        setRangeTo('');
        setUseRange(false);
    };

    const handleCommaSubmit = () => {
        if (!commaInput.trim()) {
            setRangeError('יש להזין ערכים מופרדים בפסיקים');
            return;
        }
        const items = commaInput.split(',').map(item => item.trim()).filter(item => item);
        if (items.length === 0) {
            setRangeError('לא נמצאו ערכים תקינים');
            return;
        }
        if (items.length > 500) {
            setRangeError('מספר הפריטים גדול מדי (מקסימום 500)');
            return;
        }
        onGenerateFromComma(node.id, items, node.type);
        setCommaInput('');
        setUseRange(false);
        setRangeError('');
    };

    const getTypeLabel = () => {
        const option = typeOptions.find(opt => opt.value === node.type);
        return option ? option.label : node.type;
    };

    // Render for generated items (allow siblings and children)
    if (node.isGenerated) {
        const typeLabel = typeOptions.find(opt => opt.value === node.generatedType)?.label || node.generatedType;
        return (
            <div className="tree-node" style={{ marginRight: depth * 30 + 'px' }}>
                <div className="tree-node-content generated-node">
                    {/* Collapse/Expand button */}
                    {hasChildren && (
                        <button
                            type="button"
                            className="btn tree-btn collapse-btn"
                            onClick={() => onToggleCollapse(node.id)}
                            title={isCollapsed ? "הרחב" : "כווץ"}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                {isCollapsed ? (
                                    <polyline points="9 18 15 12 9 6"></polyline>
                                ) : (
                                    <polyline points="6 9 12 15 18 9"></polyline>
                                )}
                            </svg>
                            {isCollapsed && childCount > 0 && (
                                <span className="collapse-count">{childCount}</span>
                            )}
                        </button>
                    )}
                    {!hasChildren && <div className="collapse-placeholder"></div>}

                    <span className="tree-node-index">{index}</span>

                    {/* Add Sibling button */}
                    <button
                        type="button"
                        className="btn tree-btn add-sibling-btn"
                        onClick={() => onAddSibling(node.id, node.generatedType)}
                        title="הוסף נושא (באותה רמה)"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                    </button>

                    <span className="generated-siman-label">{typeLabel} {node.name}</span>

                    <div className="tree-node-actions-end">
                        {canAddChild && (
                            <button
                                type="button"
                                className="btn tree-btn add-child-btn"
                                onClick={() => onAddChild(node.id, node.generatedType)}
                                title="הוסף תת נושא (רמה פנימית)"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                                תת נושא
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn tree-btn delete-btn"
                            onClick={() => onDelete(node.id)}
                            title="מחק"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                </div>
                {hasChildren && !isCollapsed && (
                    <div className="tree-children">
                        {node.children.map((child, childIndex) => (
                            <TreeNode
                                key={child.id}
                                node={child}
                                index={`${index}.${childIndex + 1}`}
                                depth={depth + 1}
                                onAddSibling={onAddSibling}
                                onAddChild={onAddChild}
                                onUpdate={onUpdate}
                                onDelete={onDelete}
                                onGenerateRange={onGenerateRange}
                                onGenerateFromComma={onGenerateFromComma}
                                onToggleCollapse={onToggleCollapse}
                                collapsedNodes={collapsedNodes}
                                parentType={node.generatedType}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="tree-node" style={{ marginRight: depth * 30 + 'px' }}>
            <div className="tree-node-content">
                {/* Collapse/Expand button */}
                {hasChildren && (
                    <button
                        type="button"
                        className="btn tree-btn collapse-btn"
                        onClick={() => onToggleCollapse(node.id)}
                        title={isCollapsed ? "הרחב" : "כווץ"}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            {isCollapsed ? (
                                <polyline points="9 18 15 12 9 6"></polyline>
                            ) : (
                                <polyline points="6 9 12 15 18 9"></polyline>
                            )}
                        </svg>
                        {isCollapsed && childCount > 0 && (
                            <span className="collapse-count">{childCount}</span>
                        )}
                    </button>
                )}
                {!hasChildren && <div className="collapse-placeholder"></div>}

                <span className="tree-node-index">{index}</span>

                {/* Add Sibling button - moved before dropdown */}
                <button
                    type="button"
                    className="btn tree-btn add-sibling-btn"
                    onClick={() => onAddSibling(node.id, node.type)}
                    title="הוסף נושא (באותה רמה)"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                </button>

                <select
                    className="tree-node-select"
                    value={node.type}
                    onChange={handleTypeChange}
                >
                    {typeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>

                {/* Range checkbox and options */}
                <>
                    {/* Range checkbox */}
                    <div className="range-checkbox-container">
                        <input
                            type="checkbox"
                            id={`range-${node.id}`}
                            checked={useRange}
                            onChange={(e) => {
                                setUseRange(e.target.checked);
                                setRangeError('');
                            }}
                            className="range-checkbox"
                        />
                        <label htmlFor={`range-${node.id}`} className="range-checkbox-label">טווח</label>
                    </div>

                    {/* Regular input when range is not checked */}
                    {!useRange && (
                        <input
                            type="text"
                            className="tree-node-input"
                            value={node.name}
                            onChange={handleNameChange}
                            placeholder="שם הפריט"
                        />
                    )}

                    {/* Range options when range is checked */}
                    {useRange && (
                        <div className="range-options-container">
                            {/* Range type selection */}
                            <div className="range-type-selector">
                                <label className="range-type-option">
                                    <input
                                        type="radio"
                                        name={`rangeType-${node.id}`}
                                        value="hebrew"
                                        checked={rangeType === 'hebrew'}
                                        onChange={() => setRangeType('hebrew')}
                                    />
                                    <span>טווח אותיות עבריות</span>
                                </label>
                                <label className="range-type-option">
                                    <input
                                        type="radio"
                                        name={`rangeType-${node.id}`}
                                        value="comma"
                                        checked={rangeType === 'comma'}
                                        onChange={() => setRangeType('comma')}
                                    />
                                    <span>פיצול לפי פסיקים</span>
                                </label>
                            </div>

                            {/* Hebrew range inputs */}
                            {rangeType === 'hebrew' && (
                                <div className="siman-range-container">
                                    <div className="siman-range-inputs">
                                        <input
                                            type="text"
                                            className="tree-node-input hebrew-input"
                                            value={rangeFrom}
                                            onChange={(e) => handleHebrewInput(e, setRangeFrom)}
                                            placeholder="מ..."
                                            dir="rtl"
                                        />
                                        <span className="range-separator">עד</span>
                                        <input
                                            type="text"
                                            className="tree-node-input hebrew-input"
                                            value={rangeTo}
                                            onChange={(e) => handleHebrewInput(e, setRangeTo)}
                                            placeholder="עד..."
                                            dir="rtl"
                                        />
                                        <button
                                            type="button"
                                            className="btn tree-btn submit-siman-btn"
                                            onClick={handleRangeSubmit}
                                            title={`צור ${getTypeLabel()}`}
                                        >
                                            צור
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Comma split input */}
                            {rangeType === 'comma' && (
                                <div className="siman-range-container">
                                    <div className="siman-range-inputs comma-input-container">
                                        <input
                                            type="text"
                                            className="tree-node-input comma-input"
                                            value={commaInput}
                                            onChange={(e) => {
                                                setCommaInput(e.target.value);
                                                setRangeError('');
                                            }}
                                            placeholder="הזן ערכים מופרדים בפסיקים: א,ב,ג,ד"
                                            dir="rtl"
                                        />
                                        <button
                                            type="button"
                                            className="btn tree-btn submit-siman-btn"
                                            onClick={handleCommaSubmit}
                                            title={`צור ${getTypeLabel()}`}
                                        >
                                            צור
                                        </button>
                                    </div>
                                </div>
                            )}

                            {rangeError && <div className="siman-error">{rangeError}</div>}
                        </div>
                    )}
                </>

                {/* End actions: תת נושא and delete */}
                <div className="tree-node-actions-end">
                    {canAddChild && (
                        <button
                            type="button"
                            className="btn tree-btn add-child-btn"
                            onClick={() => onAddChild(node.id, node.type)}
                            title="הוסף תת נושא (רמה פנימית)"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                            תת נושא
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn tree-btn delete-btn"
                        onClick={() => onDelete(node.id)}
                        title="מחק"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>
            </div>
            {hasChildren && !isCollapsed && (
                <div className="tree-children">
                    {node.children.map((child, childIndex) => (
                        <TreeNode
                            key={child.id}
                            node={child}
                            index={`${index}.${childIndex + 1}`}
                            depth={depth + 1}
                            onAddSibling={onAddSibling}
                            onAddChild={onAddChild}
                            onUpdate={onUpdate}
                            onDelete={onDelete}
                            onGenerateRange={onGenerateRange}
                            onGenerateFromComma={onGenerateFromComma}
                            onToggleCollapse={onToggleCollapse}
                            collapsedNodes={collapsedNodes}
                            parentType={node.type}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// Modal component for selecting existing mapping
const CopyMappingModal = ({ isOpen, onClose, mappings, onSelect, isLoading }) => {
    const [searchTerm, setSearchTerm] = useState('');

    if (!isOpen) return null;

    const filteredMappings = mappings.filter(mapping =>
        mapping.bookName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="copy-modal-overlay" onClick={onClose}>
            <div className="copy-modal-content" onClick={e => e.stopPropagation()}>
                <div className="copy-modal-header">
                    <h2>בחר מיפוי להעתקה</h2>
                    <button className="copy-modal-close-btn" onClick={onClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                <div className="copy-modal-search">
                    <input
                        type="text"
                        placeholder="חפש לפי שם ספר..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="copy-modal-search-input"
                    />
                </div>

                <div className="copy-modal-body">
                    {isLoading ? (
                        <div className="copy-modal-loading">
                            <div className="updateStatusSpinner" style={{ width: '40px', height: '40px', borderWidth: '4px' }}></div>
                            <span>טוען מיפויים...</span>
                        </div>
                    ) : filteredMappings.length === 0 ? (
                        <div className="copy-modal-empty">
                            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                            </svg>
                            {searchTerm ? 'לא נמצאו מיפויים תואמים' : 'אין מיפויים קיימים'}
                        </div>
                    ) : (
                        <ul className="copy-mapping-list">
                            {filteredMappings.map(mapping => (
                                <li
                                    key={mapping.id}
                                    className="copy-mapping-list-item"
                                    onClick={() => onSelect(mapping)}
                                >
                                    <div className="copy-mapping-item-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                                        </svg>
                                    </div>
                                    <div className="copy-mapping-item-details">
                                        <span className="copy-mapping-item-name">{mapping.bookName}</span>
                                        <span className="copy-mapping-item-date">
                                            {mapping.createdAt ? new Date(mapping.createdAt).toLocaleDateString('he-IL') : ''}
                                        </span>
                                    </div>
                                    <div className="copy-mapping-item-arrow">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="15 18 9 12 15 6"></polyline>
                                        </svg>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

const Mipui = () => {
    const navigate = useNavigate();
    const { mipuiId } = useParams();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState("");
    const [bookName, setBookName] = useState("");
    const [treeData, setTreeData] = useState([]);
    const [collapsedNodes, setCollapsedNodes] = useState(new Set());
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });

    // New states for copy mapping feature
    const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
    const [existingMappings, setExistingMappings] = useState([]);
    const [isLoadingMappings, setIsLoadingMappings] = useState(false);

    // Initialize with first node or load existing mipui
    useEffect(() => {
        if (mipuiId) {
            loadMipui();
        } else {
            // Initialize with first empty node
            setTreeData([createNewNode()]);
        }
    }, [mipuiId]);

    const loadMipui = async () => {
        setIsLoading(true);
        try {
            const mipuiRef = doc(db, "mipui", mipuiId);
            const mipuiSnap = await getDoc(mipuiRef);

            if (mipuiSnap.exists()) {
                const data = mipuiSnap.data();
                setBookName(data.bookName || '');
                setTreeData(data.treeData || [createNewNode()]);
            } else {
                setSaveMessage({ type: 'error', text: 'לא נמצא מיפוי עם המזהה הזה' });
                setTreeData([createNewNode()]);
            }
        } catch (error) {
            console.error("Error loading mipui:", error);
            setSaveMessage({ type: 'error', text: 'שגיאה בטעינת המיפוי' });
            setTreeData([createNewNode()]);
        } finally {
            setIsLoading(false);
        }
    };

    const createNewNode = (type = "nose") => ({
        id: generateId(),
        type: type,
        name: "",
        children: [],
        dafFrom: '',
        dafTo: ''
    });

    const findAndUpdateNode = (nodes, nodeId, updateFn) => {
        return nodes.map(node => {
            if (node.id === nodeId) {
                return updateFn(node);
            }
            if (node.children && node.children.length > 0) {
                return {
                    ...node,
                    children: findAndUpdateNode(node.children, nodeId, updateFn)
                };
            }
            return node;
        });
    };

    const findParentAndAddSibling = (nodes, nodeId, newNode) => {
        for (let i = 0; i < nodes.length; i++) {
            if (nodes[i].id === nodeId) {
                const newNodes = [...nodes];
                newNodes.splice(i + 1, 0, newNode);
                return { found: true, nodes: newNodes };
            }
            if (nodes[i].children && nodes[i].children.length > 0) {
                const result = findParentAndAddSibling(nodes[i].children, nodeId, newNode);
                if (result.found) {
                    return {
                        found: true,
                        nodes: nodes.map(n =>
                            n.id === nodes[i].id
                                ? { ...n, children: result.nodes }
                                : n
                        )
                    };
                }
            }
        }
        return { found: false, nodes };
    };

    const findAndReplaceWithSimanim = (nodes, nodeId, simanim) => {
        const result = [];
        for (let i = 0; i < nodes.length; i++) {
            if (nodes[i].id === nodeId) {
                // Replace this node with the generated simanim
                result.push(...simanim);
            } else {
                const newNode = { ...nodes[i] };
                if (newNode.children && newNode.children.length > 0) {
                    newNode.children = findAndReplaceWithSimanim(newNode.children, nodeId, simanim);
                }
                result.push(newNode);
            }
        }
        return result;
    };

    const deleteNode = (nodes, nodeId) => {
        return nodes.filter(node => {
            if (node.id === nodeId) {
                return false;
            }
            if (node.children && node.children.length > 0) {
                node.children = deleteNode(node.children, nodeId);
            }
            return true;
        });
    };

    const handleAddSibling = (nodeId, currentType) => {
        const newNode = createNewNode(currentType);
        const result = findParentAndAddSibling(treeData, nodeId, newNode);
        if (result.found) {
            setTreeData(result.nodes);
        }
    };

    const handleAddChild = (nodeId, parentType) => {
        const newNode = createNewNode(parentType);
        setTreeData(findAndUpdateNode(treeData, nodeId, (node) => ({
            ...node,
            children: [...(node.children || []), newNode]
        })));
    };

    const handleUpdateNode = (nodeId, updatedNode) => {
        setTreeData(findAndUpdateNode(treeData, nodeId, () => updatedNode));
    };

    const handleDeleteNode = (nodeId) => {
        const newTreeData = deleteNode(treeData, nodeId);
        // If all nodes are deleted, add a new empty one
        if (newTreeData.length === 0) {
            setTreeData([createNewNode()]);
        } else {
            setTreeData(newTreeData);
        }
    };

    const handleGenerateRange = (nodeId, fromHebrew, toHebrew, type) => {
        const hebrewNumbers = generateHebrewRange(fromHebrew, toHebrew);
        const generatedItems = hebrewNumbers.map(hebrew => ({
            id: generateId(),
            type: type,
            name: hebrew,
            children: [],
            isGenerated: true,
            generatedType: type
        }));
        setTreeData(findAndReplaceWithSimanim(treeData, nodeId, generatedItems));
    };

    const handleGenerateFromComma = (nodeId, items, type) => {
        const generatedItems = items.map(item => ({
            id: generateId(),
            type: type,
            name: item,
            children: [],
            isGenerated: true,
            generatedType: type
        }));
        setTreeData(findAndReplaceWithSimanim(treeData, nodeId, generatedItems));
    };

    const handleToggleCollapse = (nodeId) => {
        setCollapsedNodes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(nodeId)) {
                newSet.delete(nodeId);
            } else {
                newSet.add(nodeId);
            }
            return newSet;
        });
    };

    const handleCollapseAll = () => {
        const allNodeIds = new Set();
        const collectIds = (nodes) => {
            nodes.forEach(node => {
                if (node.children && node.children.length > 0) {
                    allNodeIds.add(node.id);
                    collectIds(node.children);
                }
            });
        };
        collectIds(treeData);
        setCollapsedNodes(allNodeIds);
    };

    const handleExpandAll = () => {
        setCollapsedNodes(new Set());
    };

    // Function to fetch all existing mappings
    const fetchExistingMappings = async () => {
        setIsLoadingMappings(true);
        try {
            const mipuiCollection = collection(db, "mipui");
            const querySnapshot = await getDocs(mipuiCollection);
            const mappings = [];
            querySnapshot.forEach((doc) => {
                mappings.push({
                    id: doc.id,
                    ...doc.data()
                });
            });
            // Sort by creation date (newest first)
            mappings.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
            setExistingMappings(mappings);
        } catch (error) {
            console.error("Error fetching mappings:", error);
            setSaveMessage({ type: 'error', text: 'שגיאה בטעינת המיפויים' });
        } finally {
            setIsLoadingMappings(false);
        }
    };

    // Function to check if book name already exists
    const checkBookNameExists = async (name) => {
        try {
            const mipuiCollection = collection(db, "mipui");
            const q = query(mipuiCollection, where("bookName", "==", name.trim()));
            const querySnapshot = await getDocs(q);

            // If editing existing document, exclude it from the check
            if (mipuiId) {
                return querySnapshot.docs.some(doc => doc.id !== mipuiId);
            }
            return !querySnapshot.empty;
        } catch (error) {
            console.error("Error checking book name:", error);
            return false;
        }
    };

    // Handle opening copy modal
    const handleOpenCopyModal = () => {
        setIsCopyModalOpen(true);
        fetchExistingMappings();
    };

    // Handle selecting a mapping to copy
    const handleSelectMapping = (mapping) => {
        // Deep clone the tree data to avoid reference issues
        const clonedTreeData = JSON.parse(JSON.stringify(mapping.treeData));

        // Generate new IDs for all nodes to avoid conflicts
        const regenerateIds = (nodes) => {
            return nodes.map(node => ({
                ...node,
                id: generateId(),
                children: node.children ? regenerateIds(node.children) : []
            }));
        };

        setTreeData(regenerateIds(clonedTreeData));
        setBookName(`העתק של ${mapping.bookName}`);
        setIsCopyModalOpen(false);
        setSaveMessage({ type: 'success', text: 'המיפוי הועתק בהצלחה! שנה את שם הספר ושמור.' });
        setTimeout(() => setSaveMessage({ type: '', text: '' }), 5000);
    };

    const handleSave = async () => {
        if (!bookName.trim()) {
            setSaveMessage({ type: 'error', text: 'יש להזין שם ספר' });
            return;
        }

        setIsSaving(true);
        setSaveMessage({ type: '', text: '' });

        try {
            // Check for duplicate book name
            const nameExists = await checkBookNameExists(bookName);
            if (nameExists) {
                setSaveMessage({ type: 'error', text: 'שם הספר כבר קיים במערכת. יש לבחור שם אחר.' });
                setIsSaving(false);
                return;
            }

            const mipuiData = {
                bookName: bookName.trim(),
                treeData: treeData,
                updatedAt: new Date().getTime()
            };

            if (mipuiId) {
                // Update existing
                await setDoc(doc(db, "mipui", mipuiId), mipuiData);
                setSaveMessage({ type: 'success', text: 'המיפוי נשמר בהצלחה!' });
            } else {
                // Create new
                mipuiData.createdAt = new Date().getTime();
                const docRef = await addDoc(collection(db, "mipui"), mipuiData);
                setSaveMessage({ type: 'success', text: 'המיפוי נשמר בהצלחה!' });
                // Navigate to edit URL
                navigate(`/mipui/${docRef.id}`, { replace: true });
            }
        } catch (error) {
            console.error("Error saving mipui:", error);
            setSaveMessage({ type: 'error', text: 'שגיאה בשמירת המיפוי' });
        } finally {
            setIsSaving(false);
            // Clear message after 3 seconds
            setTimeout(() => setSaveMessage({ type: '', text: '' }), 3000);
        }
    };

    const handleLogin = (e) => {
        e.preventDefault();
        if (password === ADMIN_PASSWORD) {
            setIsAuthenticated(true);
        } else {
            alert("סיסמה שגויה");
        }
    };

    if (!isAuthenticated) {
        return (
            <div className="mipui-container">
                <div className="mipui-header">
                    <div className="logo-icon" onClick={() => navigate("/")}>
                        <svg width="135" height="49" viewBox="0 0 135 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M86.3894 46.9941H85.3384L87.4833 41.3423H84.0193V39.4869H84.9845V40.5594H88.7702L86.3894 46.9941Z" fill="#1F2B70"/>
                            <path d="M82.7686 40.5594V44.2057H81.7605V40.5594H82.7686Z" fill="#1F2B70"/>
                            <path d="M74.643 40.5594H77.5172C79.351 40.5594 80.1017 41.353 80.1017 43.5944V46.9941H77.1096V46.2112H79.0936V43.6159C79.0936 41.9 78.6218 41.3423 77.3456 41.3423H76.6056L75.6189 46.9941H74.6001L75.6189 41.3423H74.643V40.5594Z" fill="#1F2B70"/>
                        </svg>
                    </div>
                    <h1>מערכת מיפוי ספרים</h1>
                    <h3>כניסת מנהל</h3>
                </div>
                <form onSubmit={handleLogin} style={{ maxWidth: '400px', margin: '0 auto', padding: '20px' }}>
                    <div className="mb-3">
                        <input
                            type="password"
                            className="form-control"
                            placeholder="הזן סיסמת מנהל"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            style={{ textAlign: 'center' }}
                        />
                    </div>
                    <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>כניסה</button>
                </form>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="mipui-container">
                <div className="spinner-container">
                    <div className="updateStatusSpinner" style={{ width: '40px', height: '40px' }}></div>
                </div>
            </div>
        );
    }

    return (
        <div className="mipui-container">
            <div className="logo-icon" onClick={() => navigate("/")}>
                <svg width="135" height="49" viewBox="0 0 135 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M86.3894 46.9941H85.3384L87.4833 41.3423H84.0193V39.4869H84.9845V40.5594H88.7702L86.3894 46.9941Z" fill="#1F2B70"/>
                    <path d="M82.7686 40.5594V44.2057H81.7605V40.5594H82.7686Z" fill="#1F2B70"/>
                    <path d="M74.643 40.5594H77.5172C79.351 40.5594 80.1017 41.353 80.1017 43.5944V46.9941H77.1096V46.2112H79.0936V43.6159C79.0936 41.9 78.6218 41.3423 77.3456 41.3423H76.6056L75.6189 46.9941H74.6001L75.6189 41.3423H74.643V40.5594Z" fill="#1F2B70"/>
                    <path d="M73.0038 40.5594V46.9941H71.9957V40.5594H73.0038Z" fill="#1F2B70"/>
                    <path d="M65.4352 40.5594H70.465V41.3423H69.6499V46.9941H68.6419V41.3423H65.4352V40.5594Z" fill="#1F2B70"/>
                    <path d="M55.5901 40.5594H58.4643C60.2982 40.5594 61.0489 41.353 61.0489 43.5944V46.9941H58.0568V46.2112H60.0408V43.6159C60.0408 41.9 59.5689 41.3423 58.2927 41.3423H57.5527L56.5661 46.9941H55.5472L56.5661 41.3423H55.5901V40.5594Z" fill="#1F2B70"/>
                    <path d="M48.8315 40.5594H51.8987C53.5502 40.5594 54.2259 41.16 54.2259 43.3049V46.9941H53.2178V43.3156C53.2178 41.7284 52.7888 41.3423 51.6306 41.3423H49.8396V46.9941H48.8315V40.5594Z" fill="#1F2B70"/>
                    <path d="M46.7955 40.5594V46.9941H45.7874V40.5594H46.7955Z" fill="#1F2B70"/>
                    <path d="M42.0379 46.9941H40.9869L43.1318 41.3423H39.6678V39.4869H40.633V40.5594H44.4187L42.0379 46.9941Z" fill="#1F2B70"/>
                    <path d="M33.0906 40.5594H38.4635V44.3023C38.4635 46.2863 37.7343 46.9941 36.0612 46.9941H35.3856V46.2112H35.9111C36.9621 46.2112 37.4662 45.8144 37.4662 44.313V41.3423H33.0906V40.5594ZM33.1549 48.6028V43.2298H34.163V48.6028H33.1549Z" fill="#1F2B70"/>
                    <path d="M27.9071 40.5594V46.2112H28.7758V46.9941H23.3707V46.2112H26.899V41.3423H23.3707V40.5594H27.9071Z" fill="#1F2B70"/>
                    <path d="M16.5361 40.5594H21.909V44.3023C21.909 46.2863 21.1798 46.9941 19.5067 46.9941H18.8311V46.2112H19.3566C20.4076 46.2112 20.9116 45.8144 20.9116 44.313V41.3423H16.5361V40.5594ZM16.6004 48.6028V43.2298H17.6085V48.6028H16.6004Z" fill="#1F2B70"/>
                    <path d="M12.9795 46.9941H11.9285L14.0734 41.3423H10.6094V39.4869H11.5746V40.5594H15.3604L12.9795 46.9941Z" fill="#1F2B70"/>
                    <path d="M9.25981 40.5594V46.9941H8.25171V40.5594H9.25981Z" fill="#1F2B70"/>
                    <path d="M0.484871 40.5594H3.93815C5.58971 40.5594 6.35115 41.2351 6.35115 43.3049V46.9941H5.34305V43.3907C5.34305 41.8142 4.82827 41.3423 3.72366 41.3423H2.40455V46.9941H0.120239V46.2112H1.43935V41.3423H0.484871V40.5594Z" fill="#1F2B70"/>
                    <path d="M78.8004 23.4675C78.2379 23.4675 77.5854 23.1975 77.5854 22.365V19.575C77.5854 19.1025 77.9679 18.8325 78.2829 18.8325C78.3054 19.3275 78.7554 19.575 79.2054 19.575H85.7304C87.6879 19.575 88.6104 20.835 88.6104 22.6575V28.98L87.0354 32.6925L79.1154 32.6475C78.6429 31.5675 77.8554 29.565 77.8779 27.36C77.9004 25.6275 78.8904 24.5475 80.1504 23.4675H78.8004ZM79.9254 28.755H88.4529L88.2954 25.92C88.2054 24.255 87.4629 23.4675 85.8429 23.4675H80.3979C79.0029 24.66 79.0479 26.145 79.9254 28.755Z" fill="#1F2B70"/>
                    <path d="M75.749 20.6325C75.9515 21.7575 75.929 24.1425 74.399 27.2925L74.219 27.2025C75.2315 25.0425 75.1865 23.4675 73.769 23.4675H71.519C70.934 23.4675 70.3265 23.1975 70.3265 22.365V19.575C70.3265 19.1025 70.709 18.8325 70.979 18.8325C71.0465 19.35 71.384 19.575 71.9465 19.575H74.2865C75.1865 19.575 75.614 19.9125 75.749 20.6325Z" fill="#1F2B70"/>
                    <path d="M68.2405 24.165C68.2855 23.7825 68.218 23.4675 67.813 23.4675H64.7755C64.213 23.4675 63.5605 23.1975 63.5605 22.365V19.575C63.5605 19.1025 63.988 18.8325 64.258 18.8325C64.2805 19.35 64.708 19.575 65.1805 19.575H67.5205C68.5555 19.575 68.713 19.9125 68.668 21.06L68.2855 25.5825C68.1505 27.0675 69.343 29.0925 69.163 30.9375C69.118 31.7025 68.938 32.7375 67.408 32.7375L68.2405 24.165Z" fill="#1F2B70"/>
                    <path d="M58.985 19.575C60.92 19.575 61.91 20.79 61.91 22.59V28.89L61.595 32.6475H51.5375V27.6075C51.5375 25.875 53.045 24.3 53.765 23.4675H52.1675C51.65 23.4675 50.9975 23.1975 50.9975 22.365V19.575C50.9975 19.1025 51.4025 18.81 51.65 18.8325C51.695 19.35 52.1225 19.575 52.595 19.575H58.985ZM53.2025 28.755H61.7075L61.595 25.8525C61.5275 24.1875 60.83 23.4675 59.21 23.4675H53.99C53.27 24.2325 53.2025 24.5025 53.2025 25.5375V28.755Z" fill="#1F2B70"/>
                    <path d="M35.6514 23.4675C35.0664 23.4675 34.4139 23.1975 34.4139 22.365V19.575C34.4139 19.1025 34.8414 18.8325 35.1339 18.8325C35.1339 19.3725 35.6064 19.575 36.0789 19.575H43.6614C44.5839 19.575 44.7414 20.0025 44.7414 20.7V23.4675H43.3464V25.8525C43.3464 27.36 44.7864 29.0925 44.7864 30.9375C44.7864 31.7025 44.2239 32.715 43.1664 32.7375V23.4675H35.6514ZM34.5039 32.7375V29.5425C34.5039 27.72 35.1339 26.64 35.6964 26.2575C35.9664 26.0775 36.2364 26.0775 36.3714 26.1225C36.1914 26.4825 35.9664 27.2025 35.9664 27.8775C35.9664 28.89 36.2589 29.97 36.2589 31.3875C36.2589 32.2875 35.7639 32.715 34.5039 32.7375Z" fill="#1F2B70"/>
                    <path d="M24.8145 23.4675C24.297 23.4675 23.6445 23.1975 23.6445 22.365V19.575C23.6445 19.1025 24.0045 18.8325 24.297 18.8325C24.342 19.2825 24.792 19.575 25.2645 19.575H26.7945C27.717 19.575 27.897 20.0925 27.897 20.5425V23.4675H26.547L22.632 28.755H27.582C29.877 28.755 30.192 28.17 30.9795 26.8425C32.2845 24.6375 32.5545 24.4575 32.5545 23.4675H29.6745C29.157 23.4675 28.5045 23.1975 28.5045 22.365V19.575C28.5045 19.1025 28.8645 18.8325 29.157 18.8325C29.202 19.2825 29.652 19.575 30.1245 19.575H31.0695C32.217 19.575 32.712 20.0475 32.712 21.015V23.58C32.712 25.605 31.002 25.5375 31.002 30.105C31.002 32.1975 29.922 32.6475 28.0095 32.6475H21.147V30.8925C21.147 29.6325 19.572 27.2025 19.572 25.9425C19.572 24.975 20.0445 23.85 21.147 23.4675H19.9095C19.347 23.4675 18.717 23.1975 18.717 22.365V19.575C18.717 19.1025 19.122 18.8325 19.392 18.8325C19.4145 19.2825 19.8645 19.575 20.337 19.575H21.9345C22.812 19.575 23.037 20.0925 23.037 20.5425V23.4675H21.9345C21.372 23.4675 20.1345 23.8275 20.1345 25.1325C20.1345 26.64 22.137 27.4725 22.4295 28.7325L26.322 23.4675H24.8145Z" fill="#1F2B70"/>
                    <path d="M13.6264 18C14.4139 18 14.6389 18.3375 14.7289 18.8775C14.8864 19.845 14.6614 21.96 12.4339 23.1975L12.2764 23.04C12.8164 21.9825 12.9064 20.1825 12.4789 18.1125C12.8614 18.0225 13.2439 18 13.6264 18ZM14.9089 23.04C15.4489 21.9825 15.5389 20.1825 15.1114 18.1125C15.4939 18.0225 15.8764 18 16.2589 18C17.0464 18 17.2714 18.3375 17.3614 18.8775C17.5189 19.845 17.2939 21.96 15.0664 23.1975L14.9089 23.04Z" fill="#1F2B70"/>
                    <path d="M1.215 23.4675C0.6525 23.4675 0 23.1975 0 22.365V19.575C0 19.1025 0.3825 18.8325 0.6975 18.8325C0.72 19.3275 1.17 19.575 1.62 19.575H8.145C10.1025 19.575 11.025 20.835 11.025 22.6575V28.98L9.45 32.6925L1.53 32.6475C1.0575 31.5675 0.27 29.565 0.2925 27.36C0.315 25.6275 1.305 24.5475 2.565 23.4675H1.215ZM2.34 28.755H10.8675L10.71 25.92C10.62 24.255 9.8775 23.4675 8.2575 23.4675H2.8125C1.4175 24.66 1.4625 26.145 2.34 28.755Z" fill="#1F2B70"/>
                    <path d="M115.193 10.8004C113.895 10.8004 112.258 10.1232 112.258 8.03513V5.60816C112.258 4.42306 113.161 3.74586 113.895 3.74586C114.008 4.87452 115.136 5.60816 116.321 5.60816H120.159C122.472 5.60816 122.924 6.90612 122.924 8.03478V10.8004H119.538L109.719 38.8377H122.134C127.89 38.8377 128.68 37.3704 130.655 34.0408C133.928 28.5104 134.605 28.0589 134.605 25.5758H127.382C126.084 25.5758 124.447 24.8986 124.447 22.8106V15.8129C124.447 14.6278 125.35 13.9506 126.084 13.9506C126.197 15.0793 127.325 15.8129 128.51 15.8129H130.881C133.759 15.8129 135 16.998 135 19.4246V25.858C135 30.937 130.711 30.7677 130.711 42.2237C130.711 47.472 128.003 48.6006 123.206 48.6006H105.995V44.1988C105.995 41.0386 102.044 34.9438 102.044 31.7835C102.044 29.3569 103.229 26.5352 105.995 25.5758H102.891C101.48 25.5758 99.9 24.8986 99.9 22.8106V15.8129C99.9 14.6278 100.916 13.9506 101.593 13.9506C101.649 15.0793 102.778 15.8129 103.963 15.8129H107.97C110.17 15.8129 110.735 17.1109 110.735 18.2395V25.5758H107.97C106.559 25.5758 103.455 26.4788 103.455 29.7519C103.455 33.533 108.478 35.621 109.211 38.7813L118.974 10.8004H115.193Z" fill="#1F2B70"/>
                    <path d="M124.313 1.33474C124.08 1.24031 123.824 1.21549 123.576 1.26344C123.329 1.31138 123.102 1.42993 122.924 1.60397L121.275 3.20821L118.351 0.363263C118.112 0.130666 117.788 0 117.45 0C117.112 0 116.788 0.130666 116.549 0.363263L113.625 3.20821L111.976 1.60397C111.798 1.43051 111.571 1.31239 111.324 1.26453C111.076 1.21668 110.82 1.24125 110.587 1.33514C110.354 1.42902 110.155 1.58801 110.015 1.79199C109.875 1.99598 109.8 2.2358 109.8 2.48115V9.04823C109.801 9.87057 110.137 10.6589 110.735 11.2404C111.332 11.8219 112.142 12.149 112.987 12.15H121.912C122.758 12.149 123.568 11.8219 124.165 11.2404C124.763 10.6589 125.099 9.87057 125.1 9.04823V2.48115C125.1 2.23578 125.025 1.9959 124.885 1.79185C124.745 1.58779 124.546 1.42871 124.313 1.33474Z" fill="#1F2B70"/>
                </svg>
            </div>

            <div className="mipui-form">
                <div className="form-headers">
                    <h1>מיפוי ספר</h1>
                    <h3>צור תוכן עניינים לספר שלך</h3>
                </div>

                {/* Book Name Section - Enhanced Design */}
                <div className="book-name-section-enhanced">
                    <div className="book-name-card">
                        <div className="book-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#5DC9E0" strokeWidth="1.5">
                                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                                <line x1="8" y1="6" x2="16" y2="6"/>
                                <line x1="8" y1="10" x2="14" y2="10"/>
                            </svg>
                        </div>
                        <div className="book-name-input-wrapper">
                            <label className="book-name-label-enhanced">שם הספר</label>
                            <input
                                type="text"
                                className="book-name-input-enhanced"
                                value={bookName}
                                onChange={(e) => setBookName(e.target.value)}
                                placeholder="הכנס את שם הספר שלך..."
                            />
                        </div>
                    </div>

                    {/* Copy Existing Mapping Button */}
                    <button
                        type="button"
                        className="btn new-sium-button copy-mapping-btn"
                        onClick={handleOpenCopyModal}
                        title="העתק מיפוי קיים"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                        העתק מיפוי קיים
                    </button>
                </div>

                <div className="tree-section">
                    <div className="tree-header">
                        <h2>תוכן העניינים</h2>
                        <div className="tree-header-actions">
                            <button
                                type="button"
                                className="btn tree-btn collapse-all-btn"
                                onClick={handleCollapseAll}
                                title="כווץ הכל"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="4 14 10 14 10 20"></polyline>
                                    <polyline points="20 10 14 10 14 4"></polyline>
                                    <line x1="14" y1="10" x2="21" y2="3"></line>
                                    <line x1="3" y1="21" x2="10" y2="14"></line>
                                </svg>
                                כווץ הכל
                            </button>
                            <button
                                type="button"
                                className="btn tree-btn expand-all-btn"
                                onClick={handleExpandAll}
                                title="הרחב הכל"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="15 3 21 3 21 9"></polyline>
                                    <polyline points="9 21 3 21 3 15"></polyline>
                                    <line x1="21" y1="3" x2="14" y2="10"></line>
                                    <line x1="3" y1="21" x2="10" y2="14"></line>
                                </svg>
                                הרחב הכל
                            </button>
                        </div>
                    </div>

                    <div className="tree-container">
                        {treeData.map((node, index) => (
                            <TreeNode
                                key={node.id}
                                node={node}
                                index={String(index + 1)}
                                depth={1}
                                onAddSibling={handleAddSibling}
                                onAddChild={handleAddChild}
                                onUpdate={handleUpdateNode}
                                onDelete={handleDeleteNode}
                                onGenerateRange={handleGenerateRange}
                                onGenerateFromComma={handleGenerateFromComma}
                                onToggleCollapse={handleToggleCollapse}
                                collapsedNodes={collapsedNodes}
                                parentType={null}
                            />
                        ))}
                    </div>

                    {/* Save Section */}
                    <div className="save-section">
                        {saveMessage.text && (
                            <div className={`save-message ${saveMessage.type}`}>
                                {saveMessage.text}
                            </div>
                        )}
                        <button
                            type="button"
                            className="btn new-sium-button save-mipui-btn"
                            onClick={handleSave}
                            disabled={isSaving}
                        >
                            {isSaving ? (
                                <div className="updateStatusSpinner"></div>
                            ) : (
                                <>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                                        <polyline points="17 21 17 13 7 13 7 21"/>
                                        <polyline points="7 3 7 8 15 8"/>
                                    </svg>
                                    {mipuiId ? 'עדכן מיפוי' : 'שמור מיפוי'}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <div className="sium-bottom-page">
                <a className="margin-left-10 no-link" href="/public" onClick={(e) => { e.preventDefault(); navigate('/'); }}>חזרה לדף הבית</a>
                <span className="margin-left-10">|</span>
                <span> © כל הזכויות שמורות לאתר סיום הש"ס</span>
            </div>

            {/* Copy Mapping Modal */}
            <CopyMappingModal
                isOpen={isCopyModalOpen}
                onClose={() => setIsCopyModalOpen(false)}
                mappings={existingMappings}
                onSelect={handleSelectMapping}
                isLoading={isLoadingMappings}
            />
        </div>
    );
};

export default Mipui;
